import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("loading"); // loading | ok | error
  const [msg, setMsg] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) { setState("error"); setMsg("Lien invalide."); return; }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        await refresh();
        setState("ok");
        setTimeout(() => navigate("/enseignant", { replace: true }), 1500);
      } catch (e) {
        setState("error");
        setMsg(formatApiErrorDetail(e.response?.data?.detail) || "Lien invalide ou expiré.");
      }
    })();
  }, [params, navigate, refresh]);

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-[#FAFAFA] p-4 dark:bg-[#0A0A0A]">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.06)] dark:border-white/10 dark:bg-white/[0.04]"
        data-testid="verify-card">
        <div className="mb-6 flex items-center justify-center gap-2.5"><Logo size={34} /><span className="text-base font-semibold text-slate-900 dark:text-slate-100">Le Passeport</span></div>
        {state === "loading" && <><Loader2 size={34} className="mx-auto animate-spin text-slate-400" /><p className="mt-4 text-sm text-slate-500">Activation de votre compte…</p></>}
        {state === "ok" && <div data-testid="verify-ok"><CheckCircle2 size={40} className="mx-auto text-emerald-500" /><h1 className="mt-4 text-xl font-semibold text-slate-900 dark:text-slate-100">Compte activé !</h1><p className="mt-1 text-sm text-slate-500">Redirection vers votre espace…</p></div>}
        {state === "error" && <div data-testid="verify-error"><XCircle size={40} className="mx-auto text-rose-500" /><h1 className="mt-4 text-xl font-semibold text-slate-900 dark:text-slate-100">Activation impossible</h1><p className="mt-1 text-sm text-slate-500">{msg}</p><button onClick={() => navigate("/login")} className="mt-4 text-sm font-semibold text-slate-900 hover:underline dark:text-slate-100" data-testid="verify-to-login">Retour à la connexion</button></div>}
      </motion.div>
    </div>
  );
}
